import { TranslationKey } from './translation/TranslationKey';
import { cloneDeep } from '@apollo/client/utilities';
import { CompanyEntity, forCreate, forDelete, forUpdate } from './common/entity';
import { TaskType, validateTaskType } from './task';
import { translate } from './translation/Translator';
import { TranslationLanguage } from './translation/translation';
import { User } from './user';

export const SystemCompany = {
  id: '00000000-0000-0000-0000-000000000000',
};

enum CompanyValidationError {
  name = 'Please enter a name',
}

export enum CompanyStatus {
  demo = 'demo',
  trial = 'trial',
  partner = 'partner',
  active = 'active',
  expired = 'expired',
  archived = 'archived',
}

export class Company extends CompanyEntity {
  name!: string;
  settings!: CompanySettings;
  status: CompanyStatus = CompanyStatus.active;
  partner: boolean = false;

  get validTaskTypes() {
    return (Object.keys(TaskType) as TaskType[]).filter(type => validateTaskType(this, type));
  }

  constructor(obj: any) {
    super(obj.id);
    Object.assign(this, cloneDeep(obj));
    this.companyId = obj.id;
    if (!obj.settings) {
      (this.settings as any) = {
        primaryColor: '#00F28D',
        accentColor: '#00F28D',
        headerColor: '#181E29',
      };
    } else {
      const features = obj.settings?.featureToggles as FeatureToggles;

      if (features.productTransactions.mobile === null) {
        this.settings.featureToggles.productTransactions.mobile = true;
      }
      if (features.productTransactions.web === null) {
        this.settings.featureToggles.productTransactions.web = true;
      }
      if (features.productTransactions.quickActions === null) {
        this.settings.featureToggles.productTransactions.quickActions = true;
      }
      if (features.orders.customer === null) {
        this.settings.featureToggles.orders.customer = true;
      }
      if (features.orders.supplier === null) {
        this.settings.featureToggles.orders.supplier = true;
      }
      if (features.orders.quickOrder === null) {
        this.settings.featureToggles.orders.quickOrder = false;
      }
      if (features.orders.editProductsOnInbound === null) {
        this.settings.featureToggles.orders.editProductsOnInbound = true;
      }
      if (features.orders.editProductsOnOutbound === null) {
        this.settings.featureToggles.orders.editProductsOnOutbound = true;
      }
      if (features.orders.editProductsOnMove === null) {
        this.settings.featureToggles.orders.editProductsOnMove = false;
      }
      if (features.orders.editProductsOnDisposal === null) {
        this.settings.featureToggles.orders.editProductsOnDisposal = true;
      }
      if (features.orders.sendMailOnAssigned === null) {
        this.settings.featureToggles.orders.sendMailOnAssigned = false;
      }
      if (features.orders.ignoreAvailableQuantity === null) {
        this.settings.featureToggles.orders.ignoreAvailableQuantity = false;
      }
      if (features.tasks.blindCount === null) {
        this.settings.featureToggles.tasks.blindCount = true;
      }
      if (features.tasks.cycleCount === null) {
        this.settings.featureToggles.tasks.cycleCount = true;
      }
      if (features.tasks.taskOrders === null) {
        this.settings.featureToggles.tasks.taskOrders = {
          taskOrders: false,
          inbound: false,
          move: false,
          outbound: false,
          auto: false,
          refill: {
            refill: false,
            ignoreOverstock: false,
          },
        };
      }
      if (features.tasks.quickCount === null) {
        this.settings.featureToggles.tasks.quickCount = false;
      }

      if (features.orders.productBasedApproach === null) {
        this.settings.featureToggles.orders.productBasedApproach = true;
      }

      // TODO: Should we not just update the db json for existing companies when adding feature toggles?
      if (!features.containers) {
        this.settings.featureToggles.containers = {
          containers: false,
          allowBinMismatch: false,
        };
      } else {
        if (features.containers.containers === null) {
          this.settings.featureToggles.containers.containers = false;
        }
      }

      if (features.assets.assets === null) {
        this.settings.featureToggles.assets.assets = true;
      }

      if (features.tasks.taskOrders?.refill === null) {
        features.tasks.taskOrders.refill = {
          refill: false,
          ignoreOverstock: false,
        };
      }

      if (features.scan == null) {
        features.scan = {
          handleGS1ProductNumberSeparately: false,
          handleGS1LotSeparately: false,
          ignoreGS1Lot: false,
        };
      }

      if (features.kits == null) {
        features.kits = {
          kits: true,
          refillKits: false,
        };
      }
    }

    this.settings.headerColor = this.settings.headerColor || this.settings.primaryColor;
    this.settings.accentColor = this.settings.accentColor || this.settings.primaryColor;

    this.status ||= CompanyStatus.active;
  }

  forUpdate(): UpdateCompanyInput {
    return UpdateCompanyInput.from(this, UpdateCompanyInput);
  }

  forDelete(): DeleteCompanyInput {
    return DeleteCompanyInput.from(this, DeleteCompanyInput);
  }

  validate() {
    return this.validateEntity(Object.getOwnPropertyNames(this), field => {
      if (field === 'name' && !this.name) return CompanyValidationError.name;
      return null;
    });
  }

  withName(name: string) {
    this.name = name;
    return cloneDeep(this);
  }

  withSettings(settings: CompanySettings) {
    this.settings = settings;
    return this;
  }

  withPrimaryColor(color: string) {
    this.settings.primaryColor = color;
    return cloneDeep(this);
  }

  withAccentColor(value: string) {
    this.settings.accentColor = value;
    return cloneDeep(this);
  }

  withHeaderColor(value: string) {
    this.settings.headerColor = value;
    return cloneDeep(this);
  }

  withPartner(partner: boolean) {
    this.partner = partner;
    return cloneDeep(this);
  }

  withAlertsAlertFeatureToggle(value: boolean) {
    this.settings.featureToggles.alerts.alerts = value;
    return cloneDeep(this);
  }

  withOrdersOrderFeatureToggle(value: boolean) {
    this.settings.featureToggles.orders.orders = value;
    if (!value) {
      this.settings.featureToggles.orders.supplier = value;
      this.settings.featureToggles.orders.customer = value;
      this.settings.featureToggles.orders.editProductsOnInbound = value;
      this.settings.featureToggles.orders.editProductsOnOutbound = value;
      this.settings.featureToggles.orders.editProductsOnMove = value;
      this.settings.featureToggles.orders.editProductsOnDisposal = value;
      this.settings.featureToggles.orders.productSelectionFirst = value;
      this.settings.featureToggles.orders.picking = value;
    }
    return cloneDeep(this);
  }

  withOrdersPicking(value: boolean) {
    this.settings.featureToggles.orders.picking = value;
    return cloneDeep(this);
  }

  withOrdersCustomer(value: boolean) {
    this.settings.featureToggles.orders.customer = value;
    return cloneDeep(this);
  }

  withOrdersSupplier(value: boolean) {
    this.settings.featureToggles.orders.supplier = value;
    return cloneDeep(this);
  }

  withOrderSendMailOnAssigned(value: boolean) {
    this.settings.featureToggles.orders.sendMailOnAssigned = value;
    return cloneDeep(this);
  }

  withOrdersQuickOrder(value: boolean) {
    this.settings.featureToggles.orders.quickOrder = value;
    return cloneDeep(this);
  }

  withOrdersEditProductsOnInbound(value: boolean) {
    this.settings.featureToggles.orders.editProductsOnInbound = value;
    return cloneDeep(this);
  }

  withOrdersEditProductsOnOutbound(value: boolean) {
    this.settings.featureToggles.orders.editProductsOnOutbound = value;
    return cloneDeep(this);
  }

  withOrdersEditProductsOnMove(value: boolean) {
    this.settings.featureToggles.orders.editProductsOnMove = value;
    return cloneDeep(this);
  }

  withOrdersEditProductsOnDisposal(value: boolean) {
    this.settings.featureToggles.orders.editProductsOnDisposal = value;
    return cloneDeep(this);
  }

  withOrdersProductSelectionFirst(value: boolean) {
    this.settings.featureToggles.orders.productSelectionFirst = value;
    return cloneDeep(this);
  }

  withOrdersProcessCompletedOrders(value: boolean) {
    this.settings.featureToggles.orders.processCompletedOrders = value;
    return cloneDeep(this);
  }

  withOrdersIgnoreAvailableQuantity(value: boolean) {
    this.settings.featureToggles.orders.ignoreAvailableQuantity = value;
    return cloneDeep(this);
  }

  withScanRequired(value: boolean) {
    this.settings.featureToggles.scan.scanRequired = value;
    return cloneDeep(this);
  }

  withOrdersProductBasedApproach(value: boolean) {
    this.settings.featureToggles.orders.productBasedApproach = value;
    return cloneDeep(this);
  }

  withProductTransactionsProductTransactionFeatureToggle(value: boolean) {
    this.settings.featureToggles.productTransactions.productTransactions = value;
    if (!value) {
      this.withOrdersOrderFeatureToggle(false);

      this.settings.featureToggles.productTransactions.web = value;
      this.settings.featureToggles.productTransactions.mobile = value;
    }
    return cloneDeep(this);
  }

  withWebProductTransactionFeatureToggle(value: boolean) {
    this.settings.featureToggles.productTransactions.web = value;
    return cloneDeep(this);
  }

  withMobileProductTransactionFeatureToggle(value: boolean) {
    this.settings.featureToggles.productTransactions.mobile = value;
    return cloneDeep(this);
  }

  withFeatureToggleScanRequiredWhitelist(newWhitelist: string[]) {
    this.settings.featureToggles.scan.scanRequiredWhitelist = newWhitelist;
    return cloneDeep(this);
  }

  withQuickActionsProductTransactionFeatureToggle(value: boolean) {
    this.settings.featureToggles.productTransactions.quickActions = value;
    return cloneDeep(this);
  }

  withProductsFeatureToggle(value: boolean) {
    this.settings.featureToggles.products.products = value;
    return cloneDeep(this);
  }

  withProductsDisplayEmptyStockFeatureToggle(value: boolean) {
    this.settings.featureToggles.products.displayEmptyStock = value;
    return cloneDeep(this);
  }

  withProductsGS1FeatureToggle(value: boolean) {
    this.settings.featureToggles.products.gs1Barcodes = value;
    return cloneDeep(this);
  }

  withProductsProductFeatureToggle(value: boolean) {
    this.settings.featureToggles.products.products = value;
    return cloneDeep(this);
  }

  withSAPAllowUncountedFeatureToggle(value: boolean) {
    this.settings.featureToggles.sap.allowUncounted = value;
    return cloneDeep(this);
  }

  withTasksTaskFeatureToggle(value: boolean) {
    this.settings.featureToggles.tasks.tasks = value;
    if (!value) {
      this.settings.featureToggles.tasks.allowStockUpdate = value;
      this.settings.featureToggles.tasks.cycleCount = value;
      this.settings.featureToggles.tasks.blindCount = value;
      this.withTaskOrderToggle(value);
    } else if (value) {
      this.settings.featureToggles.tasks.cycleCount = true;
    }
    return cloneDeep(this);
  }

  withTasksStockUpdateToggle(value: boolean) {
    this.settings.featureToggles.tasks.allowStockUpdate = value;
    return cloneDeep(this);
  }

  withTaskOrderToggle(value: boolean) {
    this.settings.featureToggles.tasks.taskOrders.taskOrders = value;
    if (!value) {
      this.settings.featureToggles.tasks.taskOrders.inbound = value;
      this.settings.featureToggles.tasks.taskOrders.move = value;
      this.settings.featureToggles.tasks.taskOrders.outbound = value;
      this.settings.featureToggles.tasks.taskOrders.refill.refill = value;
      this.settings.featureToggles.tasks.taskOrders.auto = value;
    }
    return cloneDeep(this);
  }

  withTaskOrderAutoToggle(value: boolean) {
    this.settings.featureToggles.tasks.taskOrders.auto = value;
    return cloneDeep(this);
  }

  withTaskOrderRefillToggle(value: boolean) {
    this.settings.featureToggles.tasks.taskOrders.refill.refill = value;
    if (!value) {
      this.settings.featureToggles.tasks.taskOrders.refill.ignoreOverstock = false;
    } else {
      this.settings.featureToggles.tasks.taskOrders.inbound = false;
      this.settings.featureToggles.tasks.taskOrders.move = false;
      this.settings.featureToggles.tasks.taskOrders.outbound = false;
    }
    return cloneDeep(this);
  }

  witTaskOrderRefillIgnoreOverstockToggle(value: boolean) {
    this.settings.featureToggles.tasks.taskOrders.refill.ignoreOverstock = value;
    return cloneDeep(this);
  }

  withTaskOrderInboundToggle(value: boolean) {
    this.settings.featureToggles.tasks.taskOrders.inbound = value;
    return cloneDeep(this);
  }

  withTaskOrderMoveToggle(value: boolean) {
    this.settings.featureToggles.tasks.taskOrders.move = value;
    return cloneDeep(this);
  }

  withTaskOrderOutboundToggle(value: boolean) {
    this.settings.featureToggles.tasks.taskOrders.outbound = value;
    return cloneDeep(this);
  }

  withCompanyVentoryVisionARFeatureToggle(value: boolean) {
    this.settings.featureToggles.company.ventoryVisionAR = value;
    return cloneDeep(this);
  }

  withCompanySessionStorageFeatureToggle(value: boolean) {
    this.settings.featureToggles.company.sessionStorage = value;
    return cloneDeep(this);
  }

  withTasksCycleCountToggle(value: boolean) {
    this.settings.featureToggles.tasks.cycleCount = value;
    return cloneDeep(this);
  }

  withTasksBlindCountToggle(value: boolean) {
    this.settings.featureToggles.tasks.blindCount = value;
    return cloneDeep(this);
  }

  withTaskQuickCountToggle(value: boolean) {
    this.settings.featureToggles.tasks.quickCount = value;
    return cloneDeep(this);
  }

  withContainersFeatureToggle(value: boolean) {
    this.settings.featureToggles.containers.containers = value;
    if (!value) {
      this.withOrdersProductBasedApproach(true);
    }
    return cloneDeep(this);
  }

  withContainersAllowBinMismatchFeatureToggle(value: boolean) {
    this.settings.featureToggles.containers.allowBinMismatch = value;
    return cloneDeep(this);
  }

  withLotsFeatureToggle(value: boolean) {
    this.settings.featureToggles.lots.lots = value;
    if (!value) {
      this.withLotUniqueCompanyFeatureToggle(false);
      this.withLotUniqueProductMasterDataFeatureToggle(false);
    } else {
      this.withLotUniqueCompanyFeatureToggle(true);
    }
    return cloneDeep(this);
  }

  withLotUniqueCompanyFeatureToggle(value: boolean) {
    this.settings.featureToggles.lots.lotUniqueCompany = value;
    if (value) {
      this.settings.featureToggles.lots.lotUniqueProductMasterData = false;
    }
    return cloneDeep(this);
  }

  withLotUniqueProductMasterDataFeatureToggle(value: boolean) {
    this.settings.featureToggles.lots.lotUniqueProductMasterData = value;
    if (value) {
      this.settings.featureToggles.lots.lotUniqueCompany = false;
    }
    return cloneDeep(this);
  }

  withLotExpiryManagementFeatureToggle(value: boolean) {
    this.settings.featureToggles.lots.expiryManagement = value;
    return cloneDeep(this);
  }

  withAssetsFeatureToggle(value: boolean) {
    this.settings.featureToggles.assets.assets = value;
    if (!value) {
      this.withAssetsInboundOrderFeatureToggle(false);
    }
    return cloneDeep(this);
  }

  withAssetsInboundOrderFeatureToggle(value: boolean) {
    this.settings.featureToggles.assets.inboundOrder = value;
    return cloneDeep(this);
  }

  withKitsFeatureToggle(kits: boolean) {
    this.settings.featureToggles.kits.kits = kits;
    return cloneDeep(this);
  }

  withRefillKitsFeatureToggle(refillKits: boolean) {
    this.settings.featureToggles.kits.refillKits = refillKits;
    return cloneDeep(this);
  }

  withScanHandleGS1ProductNumberSeparately(value: boolean) {
    this.settings.featureToggles.scan.handleGS1ProductNumberSeparately = value;
    return cloneDeep(this);
  }

  withScanHandleGS1LotSeparately(value: boolean) {
    this.settings.featureToggles.scan.handleGS1LotSeparately = value;
    return cloneDeep(this);
  }

  withScanIgnoreGS1Lot(value: boolean) {
    this.settings.featureToggles.scan.ignoreGS1Lot = value;
    return cloneDeep(this);
  }

  withLanguage(language: TranslationLanguage | undefined) {
    this.settings.language = language;
    return cloneDeep(this);
  }

  withLogo(logo: string | undefined) {
    this.settings.companyLogo = logo;
    return cloneDeep(this);
  }

  static hasScanRequired(company: Company, user?: User): boolean {
    const scanToggles = company.settings.featureToggles.scan;
    const hasScanRequired = scanToggles.scanRequired || false;
    const hasScanRequiredAllowed = scanToggles.scanRequiredWhitelist?.includes(user?.id || '');
    return hasScanRequired && !hasScanRequiredAllowed;
  }
}

export class CreateCompanyInput extends forCreate(Company) {}

export class UpdateCompanyInput extends forUpdate(Company) {}

export class DeleteCompanyInput extends forDelete(Company) {}

export interface CompanySettings {
  primaryColor: string;

  headerColor: string;
  accentColor: string;

  companyImage?: string;
  companyLogo?: string;
  companyFavicon?: string;
  scanConfigurations?: ScanConfiguration[];
  featureToggles: FeatureToggles;
  language?: string;
}

export interface ScanConfiguration {
  regex: string;
  regexOption?: string;
  barcodeFormat: BarcodeFormat;
  type: ScanConfigurationType;
  typeId?: string;
}

export interface FeatureToggles {
  productTransactions: ProductTransactionFeatureToggles;
  orders: OrderFeatureToggles;
  tasks: TaskFeatureToggles;
  products: ProductFeatureToggles;
  alerts: AlertFeatureToggles;
  company: CompanyFeatureToggles;
  sap: SAPFeatureToggles;
  containers: ContainerFeatureToggles;
  lots: LotFeatureToggles;
  assets: AssetFeatureToggles;
  scan: ScanFeatureToggles;
  kits: KitFeatureToggles;
}

export interface ScanFeatureToggles {
  handleGS1ProductNumberSeparately: boolean;
  handleGS1LotSeparately: boolean;
  ignoreGS1Lot: boolean;
  scanRequired?: boolean;
  scanRequiredWhitelist?: string[];
}

export interface AssetFeatureToggles {
  assets: boolean;
  inboundOrder: boolean;
}

export interface ContainerFeatureToggles {
  containers: boolean;
  allowBinMismatch: boolean;
}

export interface SAPFeatureToggles {
  allowUncounted: boolean;
}

export interface ProductTransactionFeatureToggles {
  productTransactions: boolean;
  web: boolean;
  mobile: boolean;
  quickActions: boolean;
}

export interface CompanyFeatureToggles {
  ventoryVisionAR: boolean;
  sessionStorage: boolean;
}

export interface OrderFeatureToggles {
  orders: boolean;
  customer: boolean;
  supplier: boolean;
  quickOrder: boolean;
  editProductsOnInbound: boolean;
  editProductsOnOutbound: boolean;
  editProductsOnMove: boolean;
  editProductsOnDisposal: boolean;
  productSelectionFirst: boolean;
  sendMailOnAssigned: boolean;
  ignoreAvailableQuantity: boolean;
  picking: boolean;
  productBasedApproach: boolean;
  processCompletedOrders: boolean;
}

export interface TaskFeatureToggles {
  tasks: boolean;
  allowStockUpdate: boolean;
  cycleCount: boolean;
  blindCount: boolean;
  taskOrders: TaskOrderFeatureToggles;
  quickCount: boolean;
}

export interface RefillTaskOrderFeatureToggles {
  refill: boolean;
  ignoreOverstock: boolean;
}

export interface TaskOrderFeatureToggles {
  taskOrders: boolean;
  inbound: boolean;
  move: boolean;
  outbound: boolean;
  refill: RefillTaskOrderFeatureToggles;
  auto: boolean;
}

export interface LotFeatureToggles {
  lots: boolean;
  lotUniqueCompany: boolean;
  lotUniqueProductMasterData: boolean;
  expiryManagement: boolean;
}

export interface ProductFeatureToggles {
  products: boolean;
  container: boolean;
  gs1Barcodes: boolean;
  displayEmptyStock: boolean;
}

export interface AlertFeatureToggles {
  alerts: boolean;
}

export interface KitFeatureToggles {
  kits: boolean;
  refillKits: boolean;
}

export enum ScanConfigurationType {
  serial = 'serial',
  lpn = 'lpn',
  bin = 'bin',
  productNumber = 'productNumber',
  lot = 'lot',
  coo = 'coo',
  stockLocation = 'stockLocation',
  container = 'container',
  customField = 'customField',
}

export function scanConfigurationTypeToString(item: ScanConfigurationType) {
  switch (item) {
    case ScanConfigurationType.serial:
      return translate({ key: TranslationKey.serialNumber });
    case ScanConfigurationType.lpn:
      return translate({ key: TranslationKey.lpn });
    case ScanConfigurationType.bin:
      return translate({ key: TranslationKey.bin });
    case ScanConfigurationType.productNumber:
      return translate({ key: TranslationKey.productNumber });
    case ScanConfigurationType.lot:
      return translate({ key: TranslationKey.lot });
    case ScanConfigurationType.coo:
      return translate({ key: TranslationKey.countryOfOrigin });
    case ScanConfigurationType.stockLocation:
      return translate({ key: TranslationKey.stockLocation });
    case ScanConfigurationType.container:
      return translate({ key: TranslationKey.container });
    case ScanConfigurationType.customField:
      return translate({ key: TranslationKey.customField });
  }
}

export enum BarcodeFormat {
  /// Barcode format representing all supported formats.
  all = 'all',
  /// Barcode format unknown to the current SDK.
  unknown = 'unknown',
  /// Barcode format constant for Code 128.
  code128 = 'code128',
  /// Barcode format constant for Code 39.
  code39 = 'code39',
  /// Barcode format constant for Code 93.
  code93 = 'code93',
  /// Barcode format constant for CodaBar.
  codabar = 'codabar',
  /// Barcode format constant for Data Matrix.
  dataMatrix = 'dataMatrix',
  /// Barcode format constant for EAN-13.
  ean13 = 'ean13',
  /// Barcode format constant for EAN-8.
  ean8 = 'ean8',
  /// Barcode format constant for ITF (Interleaved Two-of-Five).
  itf = 'itf',
  /// Barcode format constant for QR Code.
  qrCode = 'qrCode',
  /// Barcode format constant for UPC-A.
  upca = 'upca',
  /// Barcode format constant for UPC-E.
  upce = 'upce',
  /// Barcode format constant for PDF-417.
  pdf417 = 'pdf417',
  /// Barcode format constant for AZTEC.
  aztec = 'aztec',
}

export function barcodeFormatToString(item: BarcodeFormat) {
  switch (item) {
    case BarcodeFormat.all:
      return 'All';
    case BarcodeFormat.unknown:
      return 'Unknown';
    case BarcodeFormat.code128:
      return 'Code 128';
    case BarcodeFormat.code39:
      return 'Code 39';
    case BarcodeFormat.code93:
      return 'Code 93';
    case BarcodeFormat.codabar:
      return 'CodaBar';
    case BarcodeFormat.dataMatrix:
      return 'Data Matrix';
    case BarcodeFormat.ean13:
      return 'EAN-13';
    case BarcodeFormat.ean8:
      return 'EAN-8';
    case BarcodeFormat.itf:
      return 'ITF';
    case BarcodeFormat.qrCode:
      return 'QR Code';
    case BarcodeFormat.upca:
      return 'UPC-A';
    case BarcodeFormat.upce:
      return 'UPC-E';
    case BarcodeFormat.pdf417:
      return 'PDF-417';
    case BarcodeFormat.aztec:
      return 'AZTEC';
  }
}
